import { Injectable, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';

export interface SyncTask {
  id: string;
  tag: string;
  data: any;
  timestamp: number;
  priority: 'low' | 'normal' | 'high';
  maxRetries: number;
  retryCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class BackgroundSyncService {
  private swUpdate = inject(SwUpdate);

  private readonly SYNC_TASKS_KEY = 'background_sync_tasks';
  private readonly DEFAULT_MAX_RETRIES = 3;

  constructor() {
    this.initializeBackgroundSync();
  }

  /**
   * Initialize background sync functionality
   */
  private initializeBackgroundSync(): void {
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      console.log('Background Sync is supported');
      this.setupSyncEventListener();
    } else {
      console.log('Background Sync is not supported');
    }
  }

  /**
   * Setup sync event listener
   */
  private setupSyncEventListener(): void {
    // This would typically be handled in the service worker
    // Here we simulate the sync event handling
    window.addEventListener('online', () => {
      this.processPendingSyncTasks();
    });
  }

  /**
   * Register a background sync task
   */
  async registerSync(tag: string, data: any, priority: 'low' | 'normal' | 'high' = 'normal'): Promise<string> {
    const task: SyncTask = {
      id: this.generateTaskId(),
      tag,
      data,
      timestamp: Date.now(),
      priority,
      maxRetries: this.DEFAULT_MAX_RETRIES,
      retryCount: 0
    };

    // Store task locally
    this.storeSyncTask(task);

    // Try to register with Service Worker
    if (await this.registerWithServiceWorker(tag)) {
      console.log('Background sync registered:', tag);
    } else {
      console.log('Background sync registration failed, will retry when online');
    }

    return task.id;
  }

  /**
   * Register sync with Service Worker
   */
  private async registerWithServiceWorker(tag: string): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;

      if ('sync' in registration) {
        await (registration as any).sync.register(tag);
        return true;
      }
    } catch (error) {
      console.error('Error registering background sync:', error);
    }

    return false;
  }

  /**
   * Store sync task locally
   */
  private storeSyncTask(task: SyncTask): void {
    const tasks = this.getSyncTasks();
    tasks.push(task);
    this.saveSyncTasks(tasks);
  }

  /**
   * Get all sync tasks
   */
  private getSyncTasks(): SyncTask[] {
    try {
      const stored = localStorage.getItem(this.SYNC_TASKS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading sync tasks:', error);
      return [];
    }
  }

  /**
   * Save sync tasks to storage
   */
  private saveSyncTasks(tasks: SyncTask[]): void {
    try {
      localStorage.setItem(this.SYNC_TASKS_KEY, JSON.stringify(tasks));
    } catch (error) {
      console.error('Error saving sync tasks:', error);
    }
  }

  /**
   * Process pending sync tasks
   */
  private async processPendingSyncTasks(): Promise<void> {
    const tasks = this.getSyncTasks();

    if (tasks.length === 0) {
      return;
    }

    console.log(`Processing ${tasks.length} pending sync tasks`);

    // Sort by priority and timestamp
    const sortedTasks = tasks.sort((a, b) => {
      const priorityOrder = { high: 3, normal: 2, low: 1 };
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
      return priorityDiff !== 0 ? priorityDiff : a.timestamp - b.timestamp;
    });

    for (const task of sortedTasks) {
      try {
        await this.processSyncTask(task);
        this.removeSyncTask(task.id);
        console.log('Successfully processed sync task:', task.id);
      } catch (error) {
        console.error('Error processing sync task:', task.id, error);
        await this.handleSyncTaskError(task);
      }
    }
  }

  /**
   * Process a single sync task
   */
  private async processSyncTask(task: SyncTask): Promise<void> {
    switch (task.tag) {
      case 'data-sync':
        await this.processDataSync(task.data);
        break;
      case 'message-sync':
        await this.processMessageSync(task.data);
        break;
      case 'file-sync':
        await this.processFileSync(task.data);
        break;
      case 'user-action-sync':
        await this.processUserActionSync(task.data);
        break;
      default:
        console.warn('Unknown sync task tag:', task.tag);
    }
  }

  /**
   * Process data synchronization
   */
  private async processDataSync(data: any): Promise<void> {
    const { endpoint, method, payload } = data;

    const response = await fetch(endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Data sync failed: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Process message synchronization
   */
  private async processMessageSync(data: any): Promise<void> {
    const { messages } = data;

    for (const message of messages) {
      await this.syncMessage(message);
    }
  }

  /**
   * Process file synchronization
   */
  private async processFileSync(data: any): Promise<void> {
    const { files, uploadEndpoint } = data;

    for (const file of files) {
      await this.syncFile(file, uploadEndpoint);
    }
  }

  /**
   * Process user action synchronization
   */
  private async processUserActionSync(data: any): Promise<void> {
    const { actions } = data;

    for (const action of actions) {
      await this.syncUserAction(action);
    }
  }

  /**
   * Sync individual message
   */
  private async syncMessage(message: any): Promise<void> {
    const response = await fetch('/api/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(message)
    });

    if (!response.ok) {
      throw new Error(`Message sync failed: ${response.status}`);
    }
  }

  /**
   * Sync individual file
   */
  private async syncFile(file: any, endpoint: string): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(endpoint, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`File sync failed: ${response.status}`);
    }
  }

  /**
   * Sync individual user action
   */
  private async syncUserAction(action: any): Promise<void> {
    const response = await fetch('/api/user-actions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(action)
    });

    if (!response.ok) {
      throw new Error(`User action sync failed: ${response.status}`);
    }
  }

  /**
   * Handle sync task error
   */
  private async handleSyncTaskError(task: SyncTask): Promise<void> {
    task.retryCount++;

    if (task.retryCount >= task.maxRetries) {
      console.error('Max retries reached for sync task:', task.id);
      this.removeSyncTask(task.id);
    } else {
      // Update task with new retry count
      const tasks = this.getSyncTasks();
      const index = tasks.findIndex(t => t.id === task.id);
      if (index !== -1) {
        tasks[index] = task;
        this.saveSyncTasks(tasks);
      }
    }
  }

  /**
   * Remove sync task
   */
  private removeSyncTask(taskId: string): void {
    const tasks = this.getSyncTasks().filter(task => task.id !== taskId);
    this.saveSyncTasks(tasks);
  }

  /**
   * Get pending sync tasks count
   */
  getPendingTasksCount(): number {
    return this.getSyncTasks().length;
  }

  /**
   * Clear all sync tasks
   */
  clearAllTasks(): void {
    this.saveSyncTasks([]);
  }

  /**
   * Get sync tasks by tag
   */
  getTasksByTag(tag: string): SyncTask[] {
    return this.getSyncTasks().filter(task => task.tag === tag);
  }

  /**
   * Cancel sync task
   */
  cancelTask(taskId: string): boolean {
    const tasks = this.getSyncTasks();
    const index = tasks.findIndex(task => task.id === taskId);

    if (index !== -1) {
      tasks.splice(index, 1);
      this.saveSyncTasks(tasks);
      return true;
    }

    return false;
  }

  /**
   * Generate unique task ID
   */
  private generateTaskId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Check if background sync is supported
   */
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype;
  }
}
